import clsx from 'clsx';
import type { ReactNode } from 'react';
import { FiGift, FiLock } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import s from './FeatureLock.module.scss';

export interface FeatureLockProps {
  /** What is locked, named the way the real section names itself: `Accounting`. */
  title: string;
  /** One line on what the section would do, so the owner knows what they are buying. */
  description?: string;
  /** The section's own icon where there is one; a padlock stands in otherwise. */
  icon?: ReactNode;
  /** The plan that does not carry it — from `useAccessGate().planName`. */
  planName: string;
  /** `null` where this deployment ships no billing; the CTA is then left off. */
  upgradeHref: string | null;
  /**
   * The plan is a lifetime one an admin granted, so there is nothing to upgrade to: a note
   * says so where the upgrade link would be.
   */
  planGranted?: boolean;
  /**
   * `page` fills the viewport where a whole route is locked; `card` sits inline where a
   * locked section is one block among others.
   */
  variant?: 'page' | 'card';
  /** How many skeleton lines stand in for the real content. */
  rows?: number;
  /**
   * Stretch to the height of whatever it is laid out beside — for a slot in a row of
   * cards, where a placeholder shorter than its neighbours leaves a hole under it and
   * reads as broken rather than as withheld. The skeleton takes up the slack, so the
   * heading stays at the top and the upgrade line at the bottom.
   *
   * Off by default: on its own in a column, a card sized to its content is the right
   * height, and stretching it would only stretch it to nothing in particular.
   */
  fill?: boolean;
  className?: string;
}

/**
 * The shape of a section the owner's plan does not carry.
 *
 * Deliberately a placeholder rather than the real component behind a blur: the real one
 * would fetch data the server is going to refuse, and a screen of muted bars reads as
 * "nothing here yet" rather than as content being withheld from view.
 *
 * Rendered only for the owner of the selected vessel — see `AccessVerdict`. Everyone else
 * gets nothing at all, so this component never has to explain a limit its reader cannot lift.
 */
export const FeatureLock = ({
  title,
  description,
  icon,
  planName,
  upgradeHref,
  planGranted = false,
  variant = 'card',
  rows = 3,
  fill = false,
  className,
}: FeatureLockProps) => (
  <section
    className={clsx(s.lock, variant === 'page' ? s.page : s.card, fill && s.fill, className)}
    aria-labelledby={`feature-lock-${title}`}
  >
    <header className={s.header}>
      <span className={s.icon} aria-hidden>
        {icon ?? <FiLock />}
      </span>
      <div className={s.titleBlock}>
        <h2 id={`feature-lock-${title}`} className={s.title}>
          {title}
        </h2>
        {description && <p className={s.description}>{description}</p>}
      </div>
      <span className={s.chip}>
        <FiLock className={s.chipIcon} aria-hidden />
        Not on your plan
      </span>
    </header>

    {/* Purely decorative: it gives the card the silhouette of the real section. */}
    <div className={s.skeleton} aria-hidden>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className={s.skeletonRow}>
          <span className={s.skeletonBar} />
          <span className={s.skeletonBarShort} />
        </div>
      ))}
    </div>

    <footer className={s.footer}>
      <p className={s.reason}>
        Your {planName} plan doesn&apos;t include {title}.
        {planGranted && ' It’s a lifetime plan, so it can’t be upgraded.'}
      </p>
      {planGranted && (
        <span className={s.chip}>
          <FiGift className={s.chipIcon} aria-hidden />
          Lifetime Plan
        </span>
      )}
      {!planGranted && upgradeHref && (
        <Link className={s.upgrade} to={upgradeHref}>
          Upgrade Plan
        </Link>
      )}
    </footer>
  </section>
);
